import { Routes, Route, BrowserRouter, Link, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { AuthProvider } from './features/auth/core/AuthProvider';
import { ProtectedRoute } from './features/auth/components/ProtectedRoute';
import { CurrentUserLink } from './features/auth/components/CurrentUserLink';
import { UserStorageScope } from './features/auth/components/UserStorageScope';

import { LandingPage } from './pages/LandingPage';
import { LoginPage } from './features/auth/pages/LoginPage';

import { DashboardLayout } from './components/layout/DashboardLayout';
import { LanguageSwitcher } from './components/layout/LanguageSwitcher';
import { WidthToggle } from './components/layout/WidthToggle';
import { PageFooter } from './components/layout/PageFooter';
import { BackToTop } from './components/layout/BackToTop';
import { UpdateNotice } from './components/UpdateNotice';
import { VersionUpdateProvider } from './features/version/core/VersionUpdateContext';
import { AssumedRoleProvider } from './features/project/core/AssumedRoleContext';
import { UiSettingsProvider } from './features/config/core/UiSettingsContext';

/* Die Dashboard-Routen werden aus der aktiven Plugin-Registry erzeugt. */
import {
  dashboardPluginRoutes,
  defaultDashboardPath,
} from './plugins/registry';

const queryClient = new QueryClient();

export function Main() {
  const { t } = useTranslation();

  const dashboardFallbackPath = defaultDashboardPath
    ? `/dashboard/${defaultDashboardPath}`
    : '/';

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <VersionUpdateProvider>
          <AuthProvider>
            <UserStorageScope>
              <UiSettingsProvider>
                <AssumedRoleProvider>
                  <br />
                  {/* the links carry classes so that small screens can swap
                the labels for icons (see theme.css) */}
                  <nav>
                    <Link
                      className="topbar-link topbar-dashboard"
                      to="/dashboard"
                    >
                      {t('topbar.dashboard')}
                    </Link>
                    <span className="topbar-sep">|</span>
                    <Link
                      className="topbar-link topbar-administration"
                      to="/dashboard/partner"
                    >
                      {t('topbar.administration')}
                    </Link>
                    <span className="topbar-sep">|</span>
                    <CurrentUserLink />
                    <span className="topbar-sep">|</span>
                    <WidthToggle />
                    <span className="topbar-sep">|</span>
                    <LanguageSwitcher />
                  </nav>
                  <UpdateNotice />
                  <br />
                  <Routes>
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/login" element={<LoginPage />} />
                    {/* the user page moved into the dashboard layout — keep
                  the old URL working for bookmarks */}
                    <Route
                      path="/user"
                      element={<Navigate to="/dashboard/user" replace />}
                    />

                    <Route
                      path="/dashboard"
                      element={
                        <ProtectedRoute>
                          <DashboardLayout />
                        </ProtectedRoute>
                      }
                    >
                      {/* Default redirect */}
                      {defaultDashboardPath && (
                        <Route
                          index
                          element={
                            <Navigate to={defaultDashboardPath} replace />
                          }
                        />
                      )}

                      {dashboardPluginRoutes.map((pluginRoute) => (
                        <Route
                          key={pluginRoute.path}
                          path={pluginRoute.path}
                          element={pluginRoute.element}
                        />
                      ))}
                    </Route>

                    <Route
                      path="*"
                      element={<Navigate to={dashboardFallbackPath} replace />}
                    />
                  </Routes>
                  <PageFooter />
                  <BackToTop />
                </AssumedRoleProvider>
              </UiSettingsProvider>
            </UserStorageScope>
          </AuthProvider>
        </VersionUpdateProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
